import { Offcanvas, Form } from "react-bootstrap";
import CustomButton from "../../components/CustomButton/CustomButton";
import { useForm } from "react-hook-form";
import ReactQuill from "react-quill-new";
import { useEffect, useState } from "react";
import WebService from "../../utility/WebService";
import { toast } from "react-toastify";

interface EventFormData {
  show: boolean;
  handleClose: () => void;
  data?: any;
}

type FormData = {
  name: string;
  description: string;
  tagId: string;
  tagName: string ;
  link: string;
  link_name: string;
  publishedAt: string;
  featured?: boolean;
  keywords?: string;
  meta_description?: string;
  slug?: string;
};
interface Tag {
  _id: string;
  name: string;
}
const AddBlog = ({ show, handleClose, data }: EventFormData) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue, // ✅ Needed for prefill
    watch,
  } = useForm<FormData>();

  const [value, setEditorValue] = useState("");
  const [isLoading, setLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [pdfFile, setPdfFile] = useState<File | string | null>(null);

  // ✅ Tag dropdown states
  const [tagsList, setTagsList] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const limit = 10;

  // ✅ Fetch paginated tags
  const fetchTags = async () => {
    try {
      const res: any = await WebService.getAPI({
        action: `tags/get-tags/BLOG`, // no pagination params
        body: null,
      });

      const tags: Tag[] = res?.data || [];
      setTagsList(tags);

      // ✅ If editing existing data, auto-select the tag
      // if (data?.tagId) {
      //   const tagExists = tags.some((tag: Tag) => tag._id === data.tagId);
      //   if (tagExists) setValue("tagId", data.tagId);
      // }
    } catch (err) {
      console.error("Error fetching tags:", err);
    }
  };

  // ✅ Load data on open
useEffect(() => {
  if (show) {
    const tagObj = data?.tagId;

    reset({
      name: data?.name || "",
      description: data?.description || "",
      tagId: tagObj?._id || tagObj || "",        // Always ID
      tagName: tagObj?.name || "",               // NEW → always name
      link: data?.link || "",
      link_name: data?.link_name || "",
      publishedAt: data?.publishedAt
        ? new Date(data.publishedAt).toISOString().slice(0, 10)
        : "",
      featured: data?.featured || false,
      keywords: data?.keywords || "",
      meta_description: data?.meta_description || "",
      slug: data?.slug || "",
    });

    setEditorValue(data?.description || "");

    if (data?.file) setPdfFile(data.file);

    fetchTags();
  } else {
    reset({});
    setPdfFile(null);
    setEditorValue("");
  }
}, [show, data]);


  // ✅ Submit handler
  const onSubmit = (request: FormData) => {
    setLoading(true);
    const formData = new FormData();
    formData.append("name", request.name);
    formData.append("description", value);
    formData.append("link", request.link);
    formData.append("link_name", watch("link_name"));
    formData.append("featured", request.featured ? "true" : "false");
    formData.append("keywords", request.keywords || "");
    formData.append("meta_description", request.meta_description || "");
    formData.append("slug", request.slug || "");
     formData.append("tagId", request.tagId);
    if (file) {
      formData.append("image", file);
    }
    if (pdfFile && pdfFile instanceof File) {
      formData.append("file", pdfFile); // only send new pdf file
    }
    if (request.publishedAt) {
      const selectedDate = new Date(request.publishedAt);
      // 👇 Add current local time so the full datetime gets saved
      const now = new Date();
      selectedDate.setHours(now.getHours(), now.getMinutes(), now.getSeconds());

      const publishedAtISO = selectedDate.toISOString();
      formData.append("publishedAt", publishedAtISO);
    }
    const apiCall = data?._id
      ? WebService.fileUploadPutAPI({
          action: `blog/edit-blogs/${data._id}`,
          body: formData,
        })
      : WebService.fileUploadAPI({
          action: `blog/save-blogs`,
          body: formData,
        });

    apiCall
      .then((res: any) => {
        toast.success(res.message);
        reset();
        handleClose();
        setFile(null);
        setPdfFile(null);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        toast.error(error.response?.data?.message || "An error occurred");
      });
  };

  return (
    <Offcanvas
      show={show}
      onHide={() => {
        reset();
        setFile(null);
        setPdfFile(null);
        handleClose();
      }}
      placement="end"
      className="offcanvas-custom"
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>{data ? "Edit Blog" : "Add Blog"}</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <Form>
          {/* Image */}
          <Form.Group className="mb-2" controlId="eventImage">
            <Form.Label className="label">Image</Form.Label>
            <Form.Control
              type="file"
              accept="image/*"
              className="custom-image-upload"
              onChange={(e) => {
                const input = e.target as HTMLInputElement;
                const file = input.files && input.files[0];
                if (file) {
                  setFile(file);
                }
              }}
            />
          </Form.Group>
          {(file || data?.image) && (
            <div className="mb-2 event-image">
              <img
                src={file ? URL.createObjectURL(file) : data?.image}
                alt="Event"
                style={{
                  maxWidth: "100%",
                  maxHeight: 200,
                  objectFit: "contain",
                }}
              />
            </div>
          )}

          {/* Name */}
          <Form.Group className="mb-2" controlId="eventName">
            <Form.Label className="label">
              Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter name"
              {...register("name", { required: "Name is required" })}
            />
            {errors.name && (
              <div className="error-message">{errors.name.message}</div>
            )}
          </Form.Group>
          <Form.Group className="mb-2" controlId="slugField">
            <Form.Label className="label">Slug</Form.Label> <span className="text-danger">*</span>
            <Form.Control
              type="text"
              placeholder="Auto-generated slug"
              {...register("slug")}
              value={watch("slug") || ""}
               disabled // ✅ keep it readonly
            />
          </Form.Group>
          {/* Link */}
          <Form.Group className="mb-2" controlId="eventLink">
            <Form.Label className="label">
              Link
              {/* <span className="text-danger">*</span> */}
            </Form.Label>
            <Form.Control
              type="url"
              placeholder="Enter link"
              {...register("link", {
                pattern: {
                  value: /^(https?:\/\/)?([\w\-])+(\.[\w\-]+)+[/#?]?.*$/,
                  message: "Enter a valid URL",
                },
              })}
            />
            {errors.link && (
              <div className="error-message">{errors.link.message}</div>
            )}
          </Form.Group>
          {/*Link Name */}
          <Form.Group className="mb-2" controlId="eventName">
            <Form.Label className="label">
              Link Name
              {/* <span className="text-danger">*</span> */}
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter link name"
              {...register("link_name")}
            />
            {errors.link_name && (
              <div className="error-message">{errors.link_name.message}</div>
            )}
          </Form.Group>
          {/* Description */}
          <Form.Group className="mb-2" controlId="eventDescription">
            <Form.Label className="label">Description</Form.Label>
            <ReactQuill
              theme="snow"
              value={value}
              onChange={setEditorValue}
              className="editor"
            />
          </Form.Group>
          <Form.Group className="mb-2" controlId="pdfFile">
            <Form.Label className="label">Upload PDF File</Form.Label>
            <Form.Control
              type="file"
              accept=".pdf"
              className="custom-file-upload"
              onChange={(e) => {
                const input = e.target as HTMLInputElement;
                const selectedFile = input.files && input.files[0];
                if (selectedFile) {
                  setPdfFile(selectedFile);
                }
              }}
            />
          </Form.Group>

          {/* PDF Preview */}
          {pdfFile && (
            <div className="mb-2 event-file">
              <a
                href={
                  pdfFile instanceof File
                    ? URL.createObjectURL(pdfFile)
                    : (pdfFile as string)
                }
                target="_blank"
                rel="noopener noreferrer"
              >
                View PDF
              </a>
            </div>
          )}
          {/* Tags Dropdown */}
          <Form.Group className="mb-2" controlId="eventTag">
            <Form.Label className="label">
              Tag <span className="text-danger">*</span>
            </Form.Label>

            <Form.Select
              {...register("tagId", { required: "Please select a tag" })}
              size="sm"
            >
              <option value="">Select Tag</option>
              {tagsList.map((tag: { _id: string; name: string }) => (
                <option key={tag._id} value={tag._id}>
                  {tag.name}
                </option>
              ))}
            </Form.Select>

            {errors.tagId && (
              <div className="error-message">{errors.tagId.message}</div>
            )}
          </Form.Group>
          <Form.Control
            as="textarea"
            placeholder="Meta Description"
            {...register("meta_description", {
              required: "Meta description is required",
            })}
          />
          {errors.meta_description && (
            <div className="error-message">
              {errors.meta_description.message}
            </div>
          )}
          <Form.Group className="mb-2" controlId="eventName">
            <Form.Label className="label">
              Keywords <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter keywords"
              {...register("keywords", { required: "Keywords are required" })}
            />
            {errors.keywords && (
              <div className="error-message">{errors.keywords.message}</div>
            )}
          </Form.Group>
          {/* <Form.Group className="mb-2" controlId="isFeatured">
            <Form.Check
              type="checkbox"
              label="Featured"
              {...register("featured")}
            />
          </Form.Group> */}
          <Form.Group className="mb-2" controlId="eventDate">
            <Form.Label className="label">Date</Form.Label>
            <Form.Control
              type="date"
              {...register("publishedAt", { required: "Date is required" })}
            />
            {errors.publishedAt && (
              <div className="error-message">{errors.publishedAt.message}</div>
            )}
          </Form.Group>
        </Form>
      </Offcanvas.Body>
      <div className="d-flex justify-content-end p-3">
        <CustomButton
          isLoading={isLoading}
          label="Save"
          className="w-100"
          onClick={handleSubmit(onSubmit)}
        />
      </div>
    </Offcanvas>
  );
};

export default AddBlog;
